import {CustomModal} from '_metronic/partials/modals/CustomModal'
import {FC, useEffect, useState} from 'react'
import Input from '../Input'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {restrictedDomains} from 'app/modules/auth/core/restrictedDomain'
import {collection, addDoc, updateDoc, Timestamp} from '@firebase/firestore'
import db from 'services/firebase'
import {toast} from 'react-toastify'
import {decryptId} from '../../utils/helpers'

type Props = {
  isModalOpen: boolean
  hideModal: () => void
  encryptedTenantId: string
  companyName: string
  privacyPolicyLink: string
}

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .min(3, 'Minimum 8 characters')
    .max(50, 'Maximum 50 characters')
    .required('Email is required')
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]+$/, 'Please enter a valid email address')
    .test('is-work-email', 'Please use your company work email', (value) => {
      const domain = value.split('@')[1]?.toLowerCase()
      if (!domain) return false
      return !restrictedDomains.includes(domain)
    }),
})

const SubscribeModal: FC<Props> = ({
  isModalOpen,
  hideModal,
  encryptedTenantId,
  companyName,
  privacyPolicyLink,
}) => {
  const [tenantId, setTenantId] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => setTenantId(decryptId(encryptedTenantId)), [encryptedTenantId])

  const formik = useFormik({
    initialValues: {email: ''},
    validationSchema,
    onSubmit: async (values) => {
      setIsLoading(true)
      try {
        const subscribersRef = collection(
          db,
          'tenants',
          tenantId,
          'trust_center',
          'subscribers',
          'all_subscribers'
        )

        const newDocRef = await addDoc(subscribersRef, {
          email: values.email,
          subscribedOn: Timestamp.fromDate(new Date()),
        })

        await updateDoc(newDocRef, {
          id: newDocRef.id,
        })

        toast.success('Subscription for updates has been sent successfully', {
          position: toast.POSITION.TOP_CENTER,
          className: 'bg-white',
          style: {
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e0e0e0',
            color: 'black',
          },
          progressStyle: {
            background: 'black',
          },
          icon: () => (
            <svg
              width='26'
              height='26'
              viewBox='0 0 24 24'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
              style={{color: '#0E9F6E'}}
            >
              <path
                d='M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM10 17L5 12L6.41 10.59L10 14.17L17.59 6.58L19 8L10 17Z'
                fill='black'
              />
            </svg>
          ),
          autoClose: 3000,
        })
      } catch (error) {
        console.error('Error while adding subscriber for trust center')
      } finally {
        setIsLoading(false)
        hideModal()
      }
    },
  })

  const okBtnClickHandler = () => {
    formik.submitForm()
  }

  return (
    <CustomModal
      isOpen={isModalOpen}
      onClose={hideModal}
      title={'Subscribe to updates'}
      isCloseIconShown={true}
      okBtnText='Subscribe'
      okBtnClickHandler={okBtnClickHandler}
      okBtnType='black'
      cancelBtnText='Cancel'
      isLoading={isLoading}
      isLoadingBtnText='Subscribing...'
      isOkBtnDisabled={formik.values.email.length === 0 || !!formik.errors.email}
    >
      <form
        action=''
        onSubmit={(e) => {
          e.preventDefault()
          okBtnClickHandler()
        }}
      >
        <div className='d-flex flex-column justify-content-center'>
          <h4
            className='fw-normal text-custom-secondary-gray'
            style={{fontSize: '14px', marginBottom: '24px'}}
          >
            Receive email notifications when {companyName} publishes important updates to their
            Trust Center.
          </h4>

          <Input
            type='email'
            label='Email'
            autoComplete='off'
            placeholder='email@company.org'
            {...formik.getFieldProps('email')}
          />

          <p className='text-custom-secondary-gray mb-0' style={{fontSize: '12px', marginTop: '8px'}}>
            By signing up for email notifications you agree to the{' '}
            <a href={privacyPolicyLink} target='_blank'>
              <span className='text-custom-secondary-gray text-decoration-underline fw-bold'>
                Privacy Policy
              </span>
            </a>
            .
          </p>
        </div>
      </form>
    </CustomModal>
  )
}

export default SubscribeModal
